/**
 * The meet printout: the matches of one meet date on a sheet a club admin
 * can stick on the wall on a social night, for players who leave their phone
 * in the bag (asked for by a club, Oct 2026).
 *
 * Pure: meet, occurrence and fixtures in, a plain model out. The dialog and
 * the printed sheet both render THIS model through ONE component
 * (MeetPrintout.vue), so the preview the admin approves is exactly what is
 * printed.
 *
 * What is printed is deliberately small: the club, the meet, when, and each
 * match with its court (where one is set), the players' names and a score
 * (or a blank to fill in by hand). No contact details, ages, photos,
 * payments or who did not turn up.
 *
 * A wall is a public place, so an under 18 shows as "First L." whatever the
 * admin can see on screen (dev decision, 7 Oct 2026, Children's Code). The
 * admin must also tick that they have the players' permission before the
 * Print button works (owner decision, 7 Oct 2026). See COMPLIANCE.html.
 *
 * Imported explicitly (house style), never relied on as an auto import.
 */
import {useDates} from '~/composables/useDates';

export const PRINT_LINK_LABEL = 'Print';
export const PRINT_DIALOG_TITLE = 'Print the matches';
export const PRINT_BUTTON_LABEL = 'Print';
export const PRINT_PERMISSION_LABEL =
  'I have permission from the players to print this and put it up where others can see it.';
export const PRINT_NO_MATCHES_LINE = 'There are no matches to print yet. Make the match-ups first.';
export const PRINT_JUNIOR_FULL_NAMES_LABEL =
  'Show full names for under 18s. I have permission from their parents or guardians to display them.';
export const PRINT_JUNIOR_FULL_NAMES_LINE = 'Under 18s show in full.';
export const PRINT_FOOTER_LINE = 'Record the results in the Tap Leagues app or at tapleagues.com.';
export const PRINT_PREVIEW_HEADING = 'Preview';
export const PRINT_PERMISSIONS_HEADING = 'Before you print';
export const PRINT_UNDER_18_LINE = "Under 18s, and anyone whose age you can't see, show as first name and initial.";

/**
 * Is this player printed in full? Only when the API says, in so many words,
 * that they are NOT under 18 (`under18 === false`). Anyone whose age the
 * admin cannot see is cut too: the API withholds `under18` (and clears the
 * raw `isJunior` tick with it) for a member of another club, so a missing
 * answer can be a child. Cutting an adult on a wall costs nothing.
 */
const printsInFull = (player) => player?.under18 === false;

const clean = (value) => String(value ?? '').trim();

/**
 * The name a player is printed under: in full, or "First L." for an under 18
 * or an unknown age. `fullNames` is the admin's second tick, confirming the
 * parents or guardians agreed (owner decision, 7 Oct 2026); it prints
 * everyone in full.
 */
export const printName = (player, {fullNames = false} = {}) => {
  let first = clean(player?.firstName);
  let last = clean(player?.lastName);
  if (!first && !last) {
    const words = clean(player?.fullName).split(/\s+/).filter(Boolean);
    first = words[0] ?? '';
    last = words.length > 1 ? words[words.length - 1] : '';
  }
  if (fullNames === true || printsInFull(player)) return [first, last].filter(Boolean).join(' ');
  if (!last) return first;
  return [first, `${last.charAt(0).toUpperCase()}.`].filter(Boolean).join(' ');
};

/**
 * The round a meet match belongs to. The stamped round (Sep 2026) wins; older
 * rows carry it on the team names ("Round 2 Team A"); anything else is round 1.
 * Same rule as the date page's match list.
 */
export const meetFixtureRound = (fixture) => {
  if (fixture?.meetRound != null) return fixture.meetRound;
  for (const name of [fixture?.homeTeam?.name, fixture?.awayTeam?.name]) {
    const match = /^Round (\d+)\b/.exec(name || '');
    if (match) return Number(match[1]);
  }
  return 1;
};

const hasResult = (fixture) => fixture?.homeTeamResult != null && fixture?.awayTeamResult != null;

/** "7 Oct 2026, 19:00 to 21:00" in the reader's own date and clock style. */
const whenLine = (occurrence, locale) => {
  const {formatDate, formatTime} = useDates();
  if (!occurrence?.startsAt) return '';
  const day = formatDate(occurrence.startsAt, locale);
  const start = formatTime(occurrence.startsAt, locale);
  const end = occurrence.endsAt ? formatTime(occurrence.endsAt, locale) : null;
  return end ? `${day}, ${start} to ${end}` : `${day}, ${start}`;
};

/** The printout model. Only names, courts and scores leave this function. */
export const buildMeetPrintout = ({meet, occurrence, fixtures, locale, juniorFullNames = false}) => {
  const nameOf = (p) => printName(p, {fullNames: juniorFullNames});
  const ordered = [...(fixtures ?? [])].sort((a, b) =>
    (new Date(a.createdAt) - new Date(b.createdAt)) || String(a.id).localeCompare(String(b.id)));

  const groups = new Map();
  for (const f of ordered) {
    const round = meetFixtureRound(f);
    if (!groups.has(round)) groups.set(round, []);
    groups.get(round).push(f);
  }

  let number = 0;
  const rounds = [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([round, rows]) => ({
      round,
      label: `Round ${round}`,
      matches: rows.map((f) => {
        number += 1;
        const result = hasResult(f);
        return {
          id: f.id,
          number,
          court: clean(f.playingAreaName),
          home: (f.homeTeam?.players ?? []).map(nameOf).filter(Boolean),
          away: (f.awayTeam?.players ?? []).map(nameOf).filter(Boolean),
          hasResult: result,
          homeScore: result ? clean(f.homeTeamResult) : '',
          awayScore: result ? clean(f.awayTeamResult) : '',
        };
      }),
    }));

  const matches = rounds.flatMap((r) => r.matches);
  return {
    brand: 'Tap Leagues',
    clubName: clean(meet?.clubName ?? meet?.club?.name),
    meetName: clean(meet?.name),
    when: whenLine(occurrence, locale),
    rounds,
    showRoundHeadings: rounds.length > 1,
    hasCourts: matches.some((m) => m.court),
    hasResults: matches.some((m) => m.hasResult),
    matchCount: matches.length,
    // Whether the default sheet cuts anyone, whichever way the under 18 tick
    // is: the dialog offers that tick only when it would change something.
    cutsAnyName: ordered.some((f) => [...(f.homeTeam?.players ?? []), ...(f.awayTeam?.players ?? [])]
      .some((p) => !printsInFull(p))),
  };
};

/** One plain sentence saying what the sheet holds, then the under 18 rule. */
export const printoutDescription = ({hasCourts, hasResults, juniorFullNames} = {}) => {
  const match = hasCourts
    ? "every match with its court and the players' names"
    : "every match with the players' names";
  const scores = hasResults ? ', and any scores already recorded' : '';
  const names = juniorFullNames === true ? PRINT_JUNIOR_FULL_NAMES_LINE : PRINT_UNDER_18_LINE;
  return `This shows the meet name, the date and time, and ${match}, by round${scores}. ${names}`;
};

/** Print only once the admin has ticked that the players agreed, and only with something to print. */
export const canPrint = (ticked, printout) => ticked === true && (printout?.matchCount ?? 0) > 0;

/**
 * Page setup for the printed sheet, in em of its base size (13pt, 4.586mm).
 *
 * Matches the API's QuestPDF exports: A4 portrait, a 10pt page margin plus
 * 20pt of padding round the content, so 30pt (10.6mm) on every side. That
 * inset is drawn by the sheet itself on every page (`padEm`), not left to
 * the print @page margin, which "Margins: none" and some print paths drop.
 *
 * Portrait, not landscape: a night is a long list of rows and portrait holds
 * about half as many again per sheet, it is how notice boards are pinned,
 * and it is what the other table exports print on.
 *
 * The pages are normally cut from heights MEASURED on the drawn sheet (see
 * MeetPrintout.vue). The figures below are the estimate used before that
 * (the server render, tests): measured in Chromium and erring tall.
 * Column widths here must match MeetPrintout.vue's colgroup.
 */
export const PRINT_PAGE = {
  heightEm: 64.76,     // 297mm
  widthEm: 45.81,      // 210mm
  padEm: 2.31,         // 30pt
  headerEm: 6.0,       // club, meet, when and the rule under them, one line each
  titleLineEm: 1.96,
  clubLineEm: 1.24,
  theadEm: 1.6,
  bandEm: 2.1,
  footerEm: 2.3,
  rowPadEm: 0.7,       // a match row's padding and border
  rowMinEm: 2.5,       // the score boxes: about 11mm tall
  nameLineEm: 1.3125,  // one line of a name (1.05em at 1.25 line height)
  nameGapEm: 0,
  nameSizeEm: 1.05,
  cellPadEm: 1,
  columnsEm: {num: 2.8, court: 5.6, score: 3.4, vs: 1.4},
  courtLineEm: 1.3,
  logoEm: 9,
  // Poppins is wider than the table below suggests for some names; measured
  // wraps line up with 95% of the column.
  fill: 0.95,
  // What a page holds: the 60.14em inside the inset, less 1.5em (7mm) spare
  // for rounding and a browser that adds a little margin of its own.
  capacityEm: 58.6,
}

// Advance widths of Poppins semibold in em, near enough to count lines.
const NARROW = new Set([...'ijlI.\'', ',']);
const SLIM = new Set([...'frt -']);
const WIDE = new Set([...'mwMW']);
const charEm = (c) => {
  if (NARROW.has(c)) return 0.28;
  if (SLIM.has(c)) return 0.38;
  if (WIDE.has(c)) return 0.9;
  if (c >= 'A' && c <= 'Z') return 0.7;
  return 0.6;
};
const textEm = (text) => [...text].reduce((sum, c) => sum + charEm(c), 0);

/**
 * How many lines a piece of text takes in a column `capacityEm` wide, wrapped
 * the way the sheet wraps: at spaces and after hyphens, and a word wider
 * than the column broken anywhere.
 */
export const linesOfText = (text, capacityEm) => {
  const words = String(text ?? '').trim().split(/(?<=-)|\s+/).filter(Boolean);
  if (!words.length) return 1;
  let lines = 1;
  let used = 0;
  for (const word of words) {
    const w = textEm(word);
    const gap = used > 0 && !/-$/.test(word) ? charEm(' ') : 0;
    if (used > 0 && used + gap + w > capacityEm) {
      lines += 1;
      used = 0;
    }
    if (w > capacityEm) {
      // Broken anywhere; the piece left over is counted as filling its line,
      // which errs tall (Chromium often pushes the next word down anyway).
      lines += Math.ceil(w / capacityEm) - 1;
      used = capacityEm;
    } else {
      used += (used > 0 ? gap : 0) + w;
    }
  }
  return lines;
};

const contentWidthEm = () => PRINT_PAGE.widthEm - 2 * PRINT_PAGE.padEm;

/** How much of one line a name may fill in a player column, in em of the name's own size. */
export const nameCapacityEm = (hasCourts) => {
  const {num, court, score, vs} = PRINT_PAGE.columnsEm;
  const fixed = num + (hasCourts ? court : 0) + 2 * score + vs;
  const side = (contentWidthEm() - fixed) / 2 - PRINT_PAGE.cellPadEm;
  return (side / PRINT_PAGE.nameSizeEm) * PRINT_PAGE.fill;
};

/** How tall one match row prints, in em: the taller of the score boxes and either side's names. */
export const matchRowEm = (match, hasCourts) => {
  const capacity = nameCapacityEm(hasCourts);
  const side = (names) => {
    if (!names?.length) return 0;
    const lines = names.reduce((sum, n) => sum + linesOfText(n, capacity), 0);
    return lines * PRINT_PAGE.nameLineEm + (names.length - 1) * PRINT_PAGE.nameGapEm;
  };
  const court = hasCourts && match.court
    ? linesOfText(match.court, (PRINT_PAGE.columnsEm.court - PRINT_PAGE.cellPadEm) * PRINT_PAGE.fill) * PRINT_PAGE.courtLineEm
    : 0;
  return Math.max(PRINT_PAGE.rowMinEm, Math.max(side(match.home), side(match.away), court) + PRINT_PAGE.rowPadEm);
};

/** The page header's height: a long meet or club name wraps beside the logo. */
const headerEm = (printout) => {
  const textWidth = contentWidthEm() - PRINT_PAGE.logoEm - 1.5;
  const titleLines = linesOfText(printout?.meetName, (textWidth / 1.7) * PRINT_PAGE.fill);
  const clubLines = printout?.clubName ? linesOfText(printout.clubName, (textWidth / 0.95) * PRINT_PAGE.fill) : 1;
  return PRINT_PAGE.headerEm + (titleLines - 1) * PRINT_PAGE.titleLineEm + (clubLines - 1) * PRINT_PAGE.clubLineEm;
};

/**
 * Pack rounds onto pages, from their heights in em of the sheet. Each round
 * is `{bandEm, rowsEm}`; every page spends `fixedEm` on its own header,
 * column header and footer and holds `capacityEm` in all.
 *
 * Rounds go onto a page whole while they fit, and a new page starts only
 * when the next round will not. A round taller than a page of its own is
 * the one thing split: it starts a fresh page, fills it, and carries on
 * over the next (`continued`), where the rounds after it pack in behind.
 *
 * Returns pages of `{parts: [{round, from, to, continued}], usedEm}`, with
 * `round` the index into `rounds` and rows `from` up to (not including) `to`.
 */
export const paginateByHeights = (rounds, {capacityEm, fixedEm}) => {
  const pages = [];
  let page = {parts: [], usedEm: fixedEm};
  const finish = () => {
    pages.push(page);
    page = {parts: [], usedEm: fixedEm};
  };
  const room = () => capacityEm - page.usedEm;

  (rounds ?? []).forEach(({bandEm = 0, rowsEm = []}, index) => {
    const total = bandEm + rowsEm.reduce((a, b) => a + b, 0);
    if (total <= capacityEm - fixedEm) {
      if (total > room()) finish();
      page.parts.push({round: index, from: 0, to: rowsEm.length, continued: false});
      page.usedEm += total;
      return;
    }
    // Taller than a page of its own.
    if (page.parts.length) finish();
    let part = null;
    rowsEm.forEach((h, i) => {
      if (part && h > room()) {
        finish();
        part = null;
      }
      if (!part) {
        part = {round: index, from: i, to: i, continued: i > 0};
        page.parts.push(part);
        page.usedEm += bandEm;
      }
      part.to = i + 1;
      page.usedEm += h;
    });
  });
  if (page.parts.length || !pages.length) pages.push(page);
  return pages;
};

/**
 * The night split into A4 pages for the wall. Heights come from `measured`
 * when the sheet has been drawn and measured (`{fixedEm, bandEm, rows}` with
 * rows keyed by match id, see MeetPrintout.vue), and from the estimate
 * otherwise (the server render, tests, a match not drawn yet). Every page
 * then draws its own header, column header and footer, so each sheet reads
 * on its own when they are pinned side by side.
 */
export const paginateMeetPrintout = (printout, measured = null) => {
  const bandEm = printout?.showRoundHeadings ? (measured?.bandEm ?? PRINT_PAGE.bandEm) : 0;
  const fixedEm = measured?.fixedEm ?? headerEm(printout) + PRINT_PAGE.theadEm + PRINT_PAGE.footerEm;
  const rowEm = (m) => measured?.rows?.[m.id] ?? matchRowEm(m, printout?.hasCourts);
  const rounds = printout?.rounds ?? [];
  const pages = paginateByHeights(
    rounds.map((r) => ({bandEm, rowsEm: r.matches.map(rowEm)})),
    {capacityEm: PRINT_PAGE.capacityEm, fixedEm},
  );
  return pages.map((p, i) => ({
    number: i + 1,
    of: pages.length,
    heightEm: p.usedEm,
    rounds: p.parts.map(({round, from, to, continued}) => {
      const r = rounds[round];
      return {...r, matches: r.matches.slice(from, to), continued, label: continued ? `${r.label} (continued)` : r.label};
    }),
  }));
};

/** "Page 2 of 3 · Rounds 3 to 5": nothing at all when the night fits one page. */
export const printPageLine = (page, printout) => {
  if (!page || page.of <= 1) return '';
  const head = `Page ${page.number} of ${page.of}`;
  if (!printout?.showRoundHeadings || !page.rounds?.length) return head;
  const first = page.rounds[0].round;
  const last = page.rounds[page.rounds.length - 1].round;
  return first === last ? `${head} · Round ${first}` : `${head} · Rounds ${first} to ${last}`;
};
